import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import TableFilters from "@/components/ui/table/data-table-filter";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import { useGetAllProductsQuery } from "@/services/productApi";
import { useGetMembershipListQuery } from "@/services/membershipsApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface RevenueReportFiltersProps {
  dateRange?: {
    start_date?: string;
    end_date?: string;
  };
  appliedDateRange?: {
    start_date?: string;
    end_date?: string;
  };
  onDateRangeChange?: (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => void;
  onResetFilters?: () => void;
  onApplyFilters?: () => void;
  filterData: Record<string, any>;
  setFilterData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  openFilter: boolean;
  setOpenFilter: React.Dispatch<React.SetStateAction<boolean>>;
  initialValue: any;
  showAdvancedFilters?: boolean;
}

export default function RevenueReportFilters({
  dateRange,
  appliedDateRange,
  onDateRangeChange,
  onResetFilters,
  onApplyFilters,
  filterData,
  setFilterData,
  openFilter,
  setOpenFilter,
  initialValue,
  showAdvancedFilters = true
}: RevenueReportFiltersProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  // Data queries
  const { data: facilitiesData } = useGetFacilitiesQuery({
    orgId: orgId,
    query: "sort_key=id&sort_order=desc"
  }, {
    skip: !orgId
  });

  const { data: productsData } = useGetAllProductsQuery({
    org_id: orgId,
    query: "sort_key=id&sort_order=desc&status=active"
  }, {
    skip: !orgId
  });

  const { data: membershipData } = useGetMembershipListQuery({ type: "default" }, {
    skip: !orgId
  });
 
  const { data: creditAddOnData } = useGetMembershipListQuery({ type: "credit_add_ons" }, {
    skip: !orgId
  });

  // Process options
  const facilityOptions = useMemo(() => {
    return Array.isArray(facilitiesData?.data)
      ? facilitiesData?.data.map((item) => ({
        id: item.id?.toString() || "",
        name: item.name,
      }))
      : [];
  }, [facilitiesData]);
  
  const facilityMultiOptions = useMemo(() => {
    return facilityOptions.map((opt) => ({
      label: opt.name,
      value: opt.id,
    }));
  }, [facilityOptions]);

  const productsOptions = useMemo(() => {
    return Array.isArray(productsData?.data)
      ? productsData?.data.map((item) => ({
        id: item.id?.toString() || "",
        name: item.product_name,
      }))
      : [];
  }, [productsData]);

  const membershipOptions = useMemo(() => {
    return Array.isArray(membershipData)
      ? membershipData.map((item) => ({
        id: item.id?.toString() || "",
        name: item.name,
      }))
      : [];
  }, [membershipData]);

  const creditAddOnOptions = useMemo(() => {
    return Array.isArray(creditAddOnData)
      ? creditAddOnData.map((item) => ({
        id: item.id?.toString() || "",
        name: item.name,
      }))
      : [];
  }, [creditAddOnData]);

  const itemTypeOptions = useMemo(() => [
    { id: "membership_plans", name: "Membership Plans" },
    { id: "products", name: "Products" },
    { id: "facilities", name: "Facilities" },
    { id: "credit_add_ons", name: "Credit Add Ons" }
  ], []);

  const itemIdOptions = useMemo(() => {
    const type = filterData?.item_type;
    switch (type) {
      case "membership_plans": return membershipOptions;
      case "products": return productsOptions;
      case "facilities": return facilityOptions;
      case  "credit_add_ons": return creditAddOnOptions;
      default: return [];
    }
  }, [filterData?.item_type, membershipOptions, productsOptions, facilityOptions, creditAddOnOptions]);

  const itemIdMultiOptions = useMemo(() => {
    return itemIdOptions.map((opt) => ({
      label: opt.name,
      value: opt.id,
    }));
  }, [itemIdOptions]);

  const itemIdLabel = useMemo(() => {
    const type = filterData?.item_type;
    switch (type) {
      case "membership_plans": return "Membership";
      case "products": return "Product";
      case "facilities": return "Facility";
      case  "credit_add_ons": return "Credit Add On";
      default: return "Item Name";
    }
  }, [filterData?.item_type]);

  function handleFilterChange(field: string, value: string | number | (string | number)[]) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = useMemo(() => [
    {
      type: "select",
      name: "item_type",
      label: "Item Type",
      options: itemTypeOptions,
      function: (value: string) => {
        setFilterData((prev) => {
          const newData: Record<string, any> = {
            ...prev,
            item_type: value,
            item_ids: [] // Clear IDs when type changes because options change
          };

          // If facility_ids is now disabled, clear it
          if (["credit_add_ons", "membership_plans", "products"].includes(value)) {
            newData.facility_ids = [];
          }

          return newData;
        });
      },
    },
    {
      type: "multiselect",
      disabled: !filterData?.item_type || filterData?.item_type === "facilities",
      name: "item_ids",
      label: itemIdLabel,
      options: itemIdMultiOptions,
      function: (value: (string | number)[]) => handleFilterChange("item_ids", value),
    },
    {
      type: "multiselect",
      name: "facility_ids",
      disabled: (filterData?.item_type === "credit_add_ons" || filterData?.item_type === "membership_plans" || filterData?.item_type === "products"),
      label: "Facilities",
      options: facilityMultiOptions,
      function: (value: (string | number)[]) => handleFilterChange("facility_ids", value),
    }
  ], [itemTypeOptions, itemIdOptions, itemIdLabel, facilityMultiOptions]);

  const handleApplyFiltersClick = () => {
    onApplyFilters?.(); 
  };

  const resetFilters = () => {
    setFilterData({});
    onResetFilters?.();
  };

  return (
    <>
      {/* Main Filter Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
              <div className="text-sm text-black flex items-center gap-1 lg:mb-0 h-8">
                <DatePickerWithRange
                  name={"Date Range"}
                  value={{
                    start_date: dateRange?.start_date ? new Date(dateRange.start_date) : undefined,
                    end_date: dateRange?.end_date ? new Date(dateRange.end_date) : undefined,
                  }}
                  onValueChange={(dates) => {
                    onDateRangeChange?.(dates);
                  }}
                  label={"Select date range"}
                  className="w-full"
                />
              </div>
              <Button
                variant={"default"}
                className="text-sm bg-primary text-black flex items-center gap-1 lg:mb-0 h-8 px-3"
                onClick={handleApplyFiltersClick}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="gap-2 flex justify-center items-center">
                        <i className="fa fa-filter"></i>
                        Apply
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Apply Filters</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Button>
              <Button
                variant={"outline"}
                className="text-sm text-black flex items-center gap-1 lg:mb-0 h-8 px-2"
                onClick={resetFilters}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="gap-2 flex justify-center items-center">
                        <i className="fa-solid fa-arrows-rotate"></i>
                        Reset
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reset Filter</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Button>
              
              {showAdvancedFilters && (
                <button
                  className="border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center"
                  onClick={() => setOpenFilter(true)}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i className="fa fa-filter"></i>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Advanced filters</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <TableFilters
          isOpen={openFilter}
          setOpen={setOpenFilter}
          initialValue={initialValue}
          filterData={filterData}
          setFilter={setFilterData}
          setSearchCriteria={(value: any) => {
            // When user clicks "Apply filters" in the modal, call parent's apply function
            if (typeof value === 'function') {
              setFilterData(value);
            } else {
              setFilterData(value);
            }
            handleApplyFiltersClick();
          }}
          filterDisplay={filterDisplay}
          setInputValue={() => {}} // Not used in this context
          onReset={resetFilters}
        />
      )}
    </>
  );
}
